import { format, parseISO } from 'date-fns';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import {
  useSearchFiltersByName,
  useSearchGetters,
} from '#/modules/search/contexts/SearchContext';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

export function useSearchDateRange() {
  const { locale } = useI18n();
  const { embedSearchWidget } = useEmbedSearchWidget();
  const searchFiltersByName = useSearchFiltersByName();

  const { getSearchTrackingData } = useSearchAnalytics();
  const [searchParams] = useSearchParams();
  const searchParamsRef = useLatest<URLSearchParams>(searchParams);

  const { getSearchRadius, getSearchQuery } = useSearchGetters();
  const searchRadiusFromState = getSearchRadius();

  const navigate = useDeferredNavigate();

  const onChangeStart = useCallback(
    (dateString?: string | null) => {
      const value = dateString === null ? undefined : dateString;
      const currentValue = searchParamsRef.current.get('startsLT');

      const filterActiveWhenSelecting = searchParamsRef.current.has(
        'endsGT',
        value,
      );

      const date = value ? parseISO(value) : undefined;
      const formatted = date ? format(date, 'yyyy-MM-dd') : undefined;

      const newRoute = searchGetRoutePathSwitch({
        locale,
        q: searchParamsRef.current.get('q') ?? getSearchQuery(),
        filtersByName: {
          ...searchFiltersByName,
          // @ts-expect-error Fix issue with type union
          endsGT: formatted,
        },
        radius: searchParamsRef.current.get('radius') ?? searchRadiusFromState,
        embedSearchWidgetId: embedSearchWidget?.id ?? null,
      });

      trackEvent('Changed Search Filter', {
        filter_name: 'endsGT',
        [filterActiveWhenSelecting ? 'deselected_value' : 'selected_value']:
          filterActiveWhenSelecting ? currentValue : value,
        ...getSearchTrackingData(),
      });

      void navigate(newRoute, {
        replace: true,
      });
    },
    [
      locale,
      embedSearchWidget?.id,
      getSearchTrackingData,
      getSearchQuery,
      navigate,
      searchFiltersByName,
      searchParamsRef,
      searchRadiusFromState,
    ],
  );

  const onChangeEnd = useCallback(
    (dateString: string | null) => {
      const value = dateString ?? undefined;
      const currentValue = searchParamsRef.current.get('startsLT');
      const filterActiveWhenSelecting = searchParamsRef.current.has(
        'startsLT',
        value,
      );

      const date = value ? parseISO(value) : undefined;
      const formatted = date ? format(date, 'yyyy-MM-dd') : undefined;

      const newRoute = searchGetRoutePathSwitch({
        locale,
        q: searchParamsRef.current.get('q') ?? getSearchQuery(),
        filtersByName: {
          ...searchFiltersByName,
          // @ts-expect-error Fix issue with type union
          startsLT: formatted,
        },
        radius: searchParamsRef.current.get('radius') ?? searchRadiusFromState,
        embedSearchWidgetId: embedSearchWidget?.id ?? null,
      });

      trackEvent('Changed Search Filter', {
        filter_name: 'startsLT',
        [filterActiveWhenSelecting ? 'deselected_value' : 'selected_value']:
          filterActiveWhenSelecting ? currentValue : value,
        ...getSearchTrackingData(),
      });

      void navigate(newRoute, {
        replace: true,
      });
    },
    [
      locale,
      embedSearchWidget?.id,
      getSearchTrackingData,
      navigate,
      searchFiltersByName,
      searchParamsRef,
      searchRadiusFromState,
      getSearchQuery,
    ],
  );

  return {
    onChangeStart,
    onChangeEnd,
    startsLT: searchParamsRef.current.get('startsLT'),
    endsGT: searchParamsRef.current.get('endsGT'),
  };
}
